import { db } from './firebase';
import { 
  doc, getDoc, setDoc, updateDoc, collection, 
  query, where, getDocs, deleteDoc, serverTimestamp,
  Timestamp, limit, orderBy, addDoc
} from 'firebase/firestore';

// Check if a user has admin role
export const isUserAdmin = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (userDoc.exists()) {
      return userDoc.data().role === 'admin';
    }
    
    return false;
  } catch (error) {
    console.error("Error checking admin status:", error);
    return false;
  }
};

// Get all users
export const getAllUsers = async () => {
  try {
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    const usersPromises = snapshot.docs.map(async (userDoc) => {
      const userData = userDoc.data();
      const uid = userDoc.id;
      
      // Get user wallet balances
      const walletRef = doc(db, 'users', uid, 'wallets', 'balances');
      const walletDoc = await getDoc(walletRef);
      
      let wallets = { walletA: 0, walletB: 0 };
      if (walletDoc.exists()) {
        wallets = walletDoc.data();
      }
      
      return {
        uid,
        ...userData,
        wallets
      };
    });
    
    // Wait for all promises to resolve
    const users = await Promise.all(usersPromises);
    
    return users;
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

// Update user role
export const updateUserRole = async (uid, role) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { role });
    return true;
  } catch (error) {
    console.error("Error updating user role:", error);
    return false;
  }
};

// Adjust wallet balance (admin override)


export const adjustWalletBalance = async (uid, wallet, newBalance) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error(`User document for UID ${uid} does not exist.`);
    }

    // Update the wallet field directly on the user document (no subcollection)
    await updateDoc(userRef, {
      [wallet]: newBalance
    });

    // Optional: log the adjustment in the transactions subcollection
    const transactionRef = collection(db, 'users', uid, 'transactions');
    await addDoc(transactionRef, {
      type: 'admin_adjust',
      wallet,
      amount: newBalance,
      timestamp: serverTimestamp(),
      adminAction: true
    });

    return newBalance;
  } catch (error) {
    console.error("Error adjusting wallet balance:", error);
    throw error;
  }
};


// Get all withdrawals
export const getAllWithdrawals = async () => {
  try {
    console.log("=== Starting to fetch all withdrawals... ===");
    
    // Get reference to withdrawals collection
    const withdrawalsRef = collection(db, 'withdrawals');
    console.log("Collection reference created");
    
    // Create query with ordering
    const q = query(withdrawalsRef, orderBy('timestamp', 'desc'));
    console.log("Query created");
    
    // Execute query
    console.log("Executing query to get withdrawal documents...");
    const snapshot = await getDocs(q);
    
    console.log("Raw withdrawals data:", snapshot.docs.length, "documents found");
    
    if (snapshot.docs.length === 0) {
      console.warn("No withdrawal documents found in the database");
      return [];
    }
    
    // Process each document
    console.log("Processing withdrawal documents...");
    const withdrawalsPromises = snapshot.docs.map(async (docSnapshot, index) => {
      try {
        console.log(`Processing document ${index + 1}/${snapshot.docs.length}, ID: ${docSnapshot.id}`);
        const data = docSnapshot.data();
        
        if (!data) {
          console.warn(`Document ${docSnapshot.id} has no data`);
          return null;
        }
        
        // If the user reference exists, fetch the user data
        let userData = null;
        if (data.userId) {
          console.log(`Fetching user data for userId: ${data.userId}`);
          const userRef = doc(db, 'users', data.userId);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            userData = {
              uid: userDoc.id,
              ...userDoc.data()
            };
            console.log(`User data found for ${data.userId}`);
          } else {
            console.warn(`User document not found for userId: ${data.userId}`);
          }
        } else {
          console.warn(`No userId found in withdrawal document ${docSnapshot.id}`);
        }

        // Ensure accountDetails is properly structured
        let accountDetails = data.accountDetails;
        if (!accountDetails) {
          console.warn(`No accountDetails found in withdrawal document ${docSnapshot.id}`);
          accountDetails = {};
        } else if (typeof accountDetails === 'string') {
          // If accountDetails is a string, assume it's the UPI ID
          console.log(`Converting string accountDetails to object for ${docSnapshot.id}`);
          accountDetails = { upiId: accountDetails };
        }
        
        // Handle timestamp conversion
        let timestamp = new Date();
        if (data.timestamp) {
          try {
            timestamp = data.timestamp.toDate();
          } catch (e) {
            console.warn(`Error converting timestamp for document ${docSnapshot.id}:`, e);
            // Keep default timestamp
          }
        } else {
          console.warn(`No timestamp found in withdrawal document ${docSnapshot.id}`);
        }
        
        // Process other timestamps
        let processedAt = null;
        if (data.processedAt) {
          try {
            processedAt = data.processedAt.toDate();
          } catch (e) {
            console.warn(`Error converting processedAt for document ${docSnapshot.id}:`, e);
          }
        }
        
        let paidAt = null;
        if (data.paidAt) {
          try {
            paidAt = data.paidAt.toDate();
          } catch (e) {
            console.warn(`Error converting paidAt for document ${docSnapshot.id}:`, e);
          }
        }
        
        // Build complete withdrawal object
        return {
          id: docSnapshot.id,
          ...data,
          accountDetails,
          user: userData,
          timestamp: timestamp,
          // Ensure these fields are always present
          status: data.status || 'pending',
          paymentStatus: data.paymentStatus || 'unpaid',
          processedAt: processedAt,
          paidAt: paidAt,
          adminNote: data.adminNote || ''
        };
      } catch (docError) {
        console.error(`Error processing withdrawal document ${docSnapshot.id}:`, docError);
        return null;
      }
    });
    
    // Wait for all promises to resolve
    const withdrawalsWithNulls = await Promise.all(withdrawalsPromises);
    
    // Filter out any nulls (from errors)
    const withdrawals = withdrawalsWithNulls.filter(w => w !== null);
    
    console.log("Processed withdrawals from service:", withdrawals.length, "valid records");
    return withdrawals;
  } catch (error) {
    console.error("Error in getAllWithdrawals:", error);
    throw new Error(`Failed to fetch withdrawals: ${error.message}`);
  }
};

// For backwards compatibility - get only pending withdrawals
export const getPendingWithdrawals = async () => {
  try {
    const allWithdrawals = await getAllWithdrawals();
    return allWithdrawals.filter(w => w.status === 'pending');
  } catch (error) {
    console.error("Error fetching pending withdrawals:", error);
    return [];
  }
};

// Approve or decline withdrawal
export const updateWithdrawalStatus = async (withdrawalId, status, adminNote = '') => {
  try {
    const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
    await updateDoc(withdrawalRef, { 
      status, 
      adminNote,
      processedAt: serverTimestamp() 
    });
    return true;
  } catch (error) {
    console.error("Error updating withdrawal status:", error);
    return false;
  }
};

// Update withdrawal payment status
export const updateWithdrawalPaymentStatus = async (withdrawalId, paymentStatus, adminNote = '') => {
  try {
    const withdrawalRef = doc(db, 'withdrawals', withdrawalId);
    await updateDoc(withdrawalRef, { 
      paymentStatus, 
      adminNote: adminNote ? adminNote : `Payment marked as ${paymentStatus}`,
      paidAt: paymentStatus === 'paid' ? serverTimestamp() : null
    });
    return true;
  } catch (error) {
    console.error("Error updating withdrawal payment status:", error);
    return false;
  }
};

// Override match result
export const overrideMatchResult = async (matchId, result) => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, { 
      result,
      manuallyOverridden: true,
      updatedAt: serverTimestamp() 
    });
    return true;
  } catch (error) {
    console.error("Error overriding match result:", error);
    return false;
  }
};

// Toggle match visibility
export const toggleMatchVisibility = async (matchId, isVisible) => {
  try {
    const matchRef = doc(db, 'matches', matchId);
    await updateDoc(matchRef, { isVisible });
    return true;
  } catch (error) {
    console.error("Error toggling match visibility:", error);
    return false;
  }
};

// Get daily user counts for the past 30 days
export const getDailyUserStats = async (days = 30) => {
  try {
    const userStatsRef = collection(db, 'userStats');
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    const q = query(
      userStatsRef,
      where('date', '>=', Timestamp.fromDate(startDate)),
      where('date', '<=', Timestamp.fromDate(endDate)),
      orderBy('date', 'asc')
    );
    
    const snapshot = await getDocs(q);
    
    const dailyStats = [];
    snapshot.forEach((doc) => {
      dailyStats.push({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date()
      });
    });
    
    return dailyStats;
  } catch (error) {
    console.error("Error fetching daily user stats:", error);
    return [];
  }
};

// Get active users (users who logged in within the past 7 days)
export const getActiveUsers = async (days = 7) => {
  try {
    const usersRef = collection(db, 'users');
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - days);
    
    const q = query(
      usersRef,
      where('lastLogin', '>=', Timestamp.fromDate(sevenDaysAgo))
    );
    
    const snapshot = await getDocs(q);
    
    const activeUsers = [];
    snapshot.forEach((doc) => {
      activeUsers.push({
        uid: doc.id,
        ...doc.data(),
        lastLogin: doc.data().lastLogin?.toDate() || new Date()
      });
    });
    
    return activeUsers;
  } catch (error) {
    console.error("Error fetching active users:", error);
    return [];
  }
};

// Increment daily user count for today
export const incrementDailyUserCount = async () => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const statsRef = doc(db, 'userStats', today.toISOString().split('T')[0]);
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      // Update existing count
      await updateDoc(statsRef, {
        userCount: statsDoc.data().userCount + 1,
        lastUpdated: serverTimestamp()
      });
    } else {
      // Create new document for today
      await setDoc(statsRef, {
        date: Timestamp.fromDate(today),
        userCount: 1,
        lastUpdated: serverTimestamp()
      });
    }
    
    return true;
  } catch (error) {
    console.error("Error incrementing daily user count:", error);
    return false;
  }
};

// Get ad engagement statistics
export const getAdEngagementStats = async (period = 'week') => {
  try {
    // This is a placeholder for actual implementation
    // In a real app, you would query analytics data or specific ad engagement collections
    const statsRef = collection(db, 'adStats');
    const snapshot = await getDocs(statsRef);
    
    const stats = [];
    snapshot.forEach((doc) => {
      stats.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    return stats;
  } catch (error) {
    console.error("Error fetching ad stats:", error);
    return [];
  }
};

// Get referral information for a specific user
export const getUserReferralInfo = async (uid) => {
  try {
    // Get the user's data first to check for referrerUID
    const userRef = doc(db, 'users', uid);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      return { error: "User not found" };
    }
    
    const userData = userDoc.data();
    let referrerInfo = null;
    
    // If the user has a referrer, get their information
    if (userData.referrerUID) {
      const referrerRef = doc(db, 'users', userData.referrerUID);
      const referrerDoc = await getDoc(referrerRef);
      
      if (referrerDoc.exists()) {
        const referrer = referrerDoc.data();
        referrerInfo = {
          uid: userData.referrerUID,
          displayName: referrer.displayName || 'No Name',
          email: referrer.email || 'No Email',
          username: referrer.username || 'No Username',
          referralCode: referrer.referralCode || 'Unknown'
        };
      }
    }
    
    // Get all users this person has referred
    const usersRef = collection(db, 'users');
    const q = query(usersRef, where('referrerUID', '==', uid));
    const snapshot = await getDocs(q);
    
    const referredUsers = snapshot.docs.map(doc => {
      const referredUserData = doc.data();
      return {
        uid: doc.id,
        displayName: referredUserData.displayName || 'No Name',
        email: referredUserData.email || 'No Email',
        username: referredUserData.username || 'No Username',
        joinDate: referredUserData.createdAt 
          ? new Date(referredUserData.createdAt.toDate()).toLocaleString() 
          : 'Unknown'
      };
    });
    
    // Get statistics
    const walletRef = doc(db, 'users', uid, 'wallets', 'sources');
    const walletDoc = await getDoc(walletRef);
    const referralEarnings = walletDoc.exists() ? (walletDoc.data().referrals || 0) : 0;
    
    return {
      user: {
        uid,
        displayName: userData.displayName || 'No Name',
        email: userData.email || 'No Email',
        username: userData.username || 'No Username',
        referralCode: userData.referralCode || 'No Code'
      },
      referrer: referrerInfo,
      referred: {
        count: referredUsers.length,
        users: referredUsers
      },
      stats: {
        totalEarnings: referralEarnings,
        expectedEarnings: referredUsers.length * 1000 // 1000 coins per referral
      }
    };
  } catch (error) {
    console.error("Error fetching user referral info:", error);
    return { error: error.message };
  }
};

// Get daily login statistics
export const getDailyLoginStats = async (days = 30) => {
  try {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - days);
    
    // Query users who logged in during the specified period
    const usersRef = collection(db, 'users');
    const q = query(
      usersRef,
      where('lastLogin', '>=', Timestamp.fromDate(startDate))
    );
    
    const snapshot = await getDocs(q);
    
    // Group by day
    const dailyStats = {};
    
    // Initialize all days with zero
    for (let i = 0; i <= days; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateString = date.toISOString().split('T')[0];
      dailyStats[dateString] = 0;
    }
    
    // Count logins per day
    snapshot.forEach(doc => {
      const userData = doc.data();
      if (userData.lastLogin) {
        const loginDate = userData.lastLogin.toDate();
        const dateString = loginDate.toISOString().split('T')[0];
        
        if (dailyStats[dateString] !== undefined) {
          dailyStats[dateString]++;
        }
      }
    });
    
    // Convert to array format for charts
    const result = Object.entries(dailyStats)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
    
    return result;
  } catch (error) {
    console.error("Error fetching daily login stats:", error);
    return [];
  }
};

// Get total login rewards distributed
export const getLoginRewardsStats = async () => {
  try {
    // Get all users
    const usersRef = collection(db, 'users');
    const userSnapshot = await getDocs(usersRef);
    
    let totalRewards = 0;
    let usersWithRewards = 0;
    let biggestRewardEarner = { uid: null, amount: 0, email: null };
    let longestStreak = { uid: null, streak: 0, email: null };
    let totalStreaks = 0;
    let usersWithStreaks = 0;
    
    // For each user, check their wallet sources and streaks
    const userPromises = userSnapshot.docs.map(async (userDoc) => {
      const uid = userDoc.id;
      const userData = userDoc.data();
      
      // Get wallet sources document
      const sourcesRef = doc(db, 'users', uid, 'wallets', 'sources');
      const sourcesSnap = await getDoc(sourcesRef);
      
      // Get streak information
      const streakRef = doc(db, 'users', uid, 'stats', 'loginStreak');
      const streakSnap = await getDoc(streakRef);
      
      let hasStreak = false;
      
      if (streakSnap.exists()) {
        const streakData = streakSnap.data();
        const currentStreak = streakData.currentStreak || 0;
        const highestStreak = streakData.highestStreak || 0;
        
        if (highestStreak > 0) {
          usersWithStreaks++;
          totalStreaks += highestStreak;
          hasStreak = true;
          
          // Track longest streak
          if (highestStreak > longestStreak.streak) {
            longestStreak = {
              uid,
              streak: highestStreak,
              email: userData.email
            };
          }
        }
      }
      
      if (sourcesSnap.exists()) {
        const sources = sourcesSnap.data();
        const loginRewards = sources.login || 0;
        
        if (loginRewards > 0) {
          usersWithRewards++;
          totalRewards += loginRewards;
          
          // Track biggest earner
          if (loginRewards > biggestRewardEarner.amount) {
            biggestRewardEarner = {
              uid,
              amount: loginRewards,
              email: userData.email,
              hasStreak
            };
          }
        }
      }
      
      return null; // Just to have something to return
    });
    
    // Wait for all promises to complete
    await Promise.all(userPromises);
    
    return {
      totalRewards,
      usersWithRewards,
      averageRewards: usersWithRewards > 0 ? Math.round(totalRewards / usersWithRewards) : 0,
      biggestRewardEarner,
      longestStreak,
      usersWithStreaks,
      averageStreak: usersWithStreaks > 0 ? Math.round(totalStreaks / usersWithStreaks) : 0
    };
  } catch (error) {
    console.error("Error fetching login rewards stats:", error);
    return {
      totalRewards: 0,
      usersWithRewards: 0,
      averageRewards: 0,
      biggestRewardEarner: { uid: null, amount: 0, email: null },
      longestStreak: { uid: null, streak: 0, email: null },
      usersWithStreaks: 0,
      averageStreak: 0
    };
  }
};

// Search users by display name, email or username
export const searchUsers = async (searchTerm) => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return await getAllUsers();
    }
    
    const searchTermLower = searchTerm.toLowerCase();
    const usersRef = collection(db, 'users');
    const snapshot = await getDocs(usersRef);
    
    // Get all users and filter them client-side (Firestore doesn't support case-insensitive search)
    const filteredUsersPromises = snapshot.docs
      .filter(doc => {
        const userData = doc.data();
        const displayName = (userData.displayName || '').toLowerCase();
        const email = (userData.email || '').toLowerCase();
        const username = (userData.username || '').toLowerCase();
        
        return displayName.includes(searchTermLower) || 
               email.includes(searchTermLower) || 
               username.includes(searchTermLower);
      })
      .map(async (userDoc) => {
        const userData = userDoc.data();
        const uid = userDoc.id;
        
        // Get user wallet balances
        const walletRef = doc(db, 'users', uid);
        const walletDoc = await getDoc(walletRef);
        
        let walletA = 0;
        let walletB = 0;
        
        if (walletDoc.exists()) {
          const userWalletData = walletDoc.data();
          walletA = userWalletData.walletA || 0;
          walletB = userWalletData.walletB || 0;
        }
        
        return {
          uid,
          ...userData,
          walletA,
          walletB
        };
      });
    
    // Wait for all promises to resolve
    const filteredUsers = await Promise.all(filteredUsersPromises);
    
    return filteredUsers;
  } catch (error) {
    console.error("Error searching users:", error);
    return [];
  }
};

// Manage daily code
export const manageDailyCode = async (code) => {
  try {
    const codeRef = doc(db, 'settings', 'dailyCode');
    await setDoc(codeRef, {
      code,
      updatedAt: serverTimestamp()
    });
    
    return { success: true, message: "Daily code updated successfully" };
  } catch (error) {
    console.error("Error managing daily code:", error);
    return { success: false, message: "Failed to update daily code" };
  }
};

// Get current daily code
export const getCurrentDailyCode = async () => {
  try {
    const codeRef = doc(db, 'settings', 'dailyCode');
    const codeDoc = await getDoc(codeRef);
    
    if (codeDoc.exists()) {
      const data = codeDoc.data();
      return {
        code: data.code,
        updatedAt: data.updatedAt?.toDate() || new Date(),
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error getting current daily code:", error);
    return null;
  }
}; 